/**
 * Services Page
 * 
 * Main interface for managing church services in the Prelude application.
 * Features include:
 * - List view of all services with filtering and sorting
 * - Service creation and editing through ServiceForm
 * - Quick view of service details and team assignments
 * - Service deletion and management
 * - Integration with calendar view
 * 
 * Provides comprehensive service management capabilities
 * with proper loading states and error handling.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Fab,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  useMediaQuery,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  MusicNote as RehearsalIcon,
  Church as ServiceIcon,
  MoreVert as MoreVertIcon,
  Event as CalendarIcon,
  Schedule as TimeIcon,
  DateRange as DateIcon,
  Group as GroupIcon,
  QueueMusic as QueueMusicIcon,
  Visibility as VisibilityIcon,
  PlayCircle as PlayCircleIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  EventBusy as EventBusyIcon,
  Sms as SmsIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { alpha, Paper } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';
import { formatDate, formatDateTime, formatTime } from '../utils/dateFormatter';
import customFetch from '../utils/axios';
import { API_ENDPOINTS } from '../config/api';
import ServiceForm from '../components/services/ServiceForm';
import PageHeader from '../components/common/PageHeader';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { generateServicePDF } from '../utils/pdfUtils';

const ServiceCard = ({ service, onEdit, onView, onDelete }) => {
  const theme = useTheme();
  const iconColor = theme.palette.primary.main;
  const { user } = useAuth();
  const [teamCount, setTeamCount] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    // Count only valid team members (those with a user)
    if (service.team) {
      const validMembers = service.team.filter(member => member.user);
      setTeamCount(validMembers.length);
    }
  }, [service.team]);

  const canEditService = user && (user.role === 'admin' || user.role === 'leader');



  const InfoElement = ({ icon, text }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      backgroundColor: 'rgba(156, 39, 176, 0.05)',
      borderRadius: '8px',
      padding: '4px 8px',
      mr: 1
    }}>
      {React.cloneElement(icon, { 
        sx: { 
          fontSize: '1.1rem', 
          color: iconColor,
          mr: 0.5
        }
      })}
      <Typography variant="body2" color="text.secondary" noWrap>
        {text}
      </Typography>
    </Box>
  );

  const TimeDisplay = ({ date, time, icon, label }) => {
    if (!date) {
      return null;
    }
    
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return null;
    }

    // Robustly parse time: handle 'HH:mm' or ISO
    let dateTimeObj = new Date(dateObj);
    if (time && time.start) {
      if (/^\d{2}:\d{2}$/.test(time.start)) {
        // 'HH:mm' string
        const [hours, minutes] = time.start.split(':');
        dateTimeObj.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      } else {
        // Try ISO or fallback
        const parsed = new Date(time.start);
        if (!isNaN(parsed)) {
          dateTimeObj = parsed;
        }
      }
    }
    
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          mb: 1
        }}>
          {React.cloneElement(icon, { 
            sx: { 
              fontSize: '1.2rem', 
              color: theme.palette.primary.main,
              mr: 1
            }
          })}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: theme.palette.primary.main,
              fontWeight: 600
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: 1,
          pl: 3
        }}>
          <InfoElement 
            icon={<CalendarIcon />} 
            text={formatDate(dateObj)}
          />
          <InfoElement 
            icon={<DateIcon />} 
            text={formatDate(dateObj)}
          />
          {time && time.start && (
            <InfoElement 
              icon={<TimeIcon />} 
              text={formatTime(dateTimeObj)}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Fade in={true}>
      <Card
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.12)',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" component="h2">
            {service.title || 'Untitled Service'}
          </Typography>
          <Box sx={{ display: 'flex' }}>
            <Tooltip title={t('common.downloadPdf') || 'Download PDF'}>
              <IconButton
                size="small"
                onClick={() => generateServicePDF(service)}
                sx={{ color: theme.palette.secondary.main }}
              >
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canEditService ? (
              <>
                <Tooltip title={t('common.edit')}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(service)}
                    sx={{ color: '#a259ff' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.delete')}>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(service)}
                    sx={{ color: '#ff5252' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title={t('common.view')}>
                <IconButton
                  size="small"
                  onClick={() => onView(service)}
                  sx={{ color: theme.palette.info.main }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip
            label={service.category || 'Uncategorized'}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
            }}
          />
          {service.author && (
            <Typography variant="body2" color="text.secondary">
              {t('common.by')} {service.author.firstName || ''} {service.author.lastName || ''}
            </Typography>
          )}
        </Box>

        <TimeDisplay 
          date={service.date}
          time={service.time}
          icon={<ServiceIcon />}
          label={t('common.service')}
        />

        {service.rehearsal && service.rehearsal.date && (
          <TimeDisplay 
            date={service.rehearsal.date}
            time={service.rehearsal.time}
            icon={<RehearsalIcon />}
            label={t('common.rehearsal')}
          />
        )}

        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <InfoElement 
              icon={<GroupIcon />} 
              text={`${teamCount} ${t('common.members')}`}
            />
            <InfoElement 
              icon={<QueueMusicIcon />} 
              text={`${service.songs?.length || 0} ${t('common.songs')}`}
            />
            {service.youtubeLink && (
              <Tooltip title="Watch on YouTube">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(service.youtubeLink, '_blank');
                  }}
                  sx={{
                    color: '#9C27B0', // Material-UI purple
                  }}
                >
                  <PlayCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Card>
    </Fade>
  );
};

const Services = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openServiceForm, setOpenServiceForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [orderBy, setOrderBy] = useState('date');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [expandedTabs, setExpandedTabs] = useState(['myServices']);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { canEdit } = useAuth();
  const { t, i18n } = useTranslation();
  const [upcomingBlockouts, setUpcomingBlockouts] = useState([]);
  const [upcomingBlockoutsLoading, setUpcomingBlockoutsLoading] = useState(false);
  const [showUpcomingBlockouts, setShowUpcomingBlockouts] = useState(false);

  const getLocale = () => {
    switch (i18n.language) {
      case 'pt': return ptBR;
      case 'es': return es;
      default: return enUS;
    }
  };

  const formatBlockoutDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'dd MMM', { locale: getLocale() });
    } catch { return dateString; }
  };

  const fetchUpcomingBlockouts = async () => {
    if (!canEdit()) return;
    setUpcomingBlockoutsLoading(true);
    try {
      const response = await customFetch.get(API_ENDPOINTS.BLOCKOUT_DATES.UPCOMING, { params: { days: 30 } });
      setUpcomingBlockouts(response.data.data || []);
    } catch (err) {
      console.error('Error fetching upcoming blockouts:', err);
    } finally {
      setUpcomingBlockoutsLoading(false);
    }
  };

  useEffect(() => {
    if (canEdit()) fetchUpcomingBlockouts();
  }, []);

  // Group blockout dates by user for display
  const groupedBlockouts = upcomingBlockouts.reduce((acc, b) => {
    const userId = b.user?._id;
    if (!userId) return acc;
    if (!acc[userId]) {
      acc[userId] = {
        user: b.user,
        dates: [],
      };
    }
    acc[userId].dates.push(b);
    return acc;
  }, {});

  const groupedBlockoutsArray = Object.values(groupedBlockouts);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    const loadServiceFromId = async () => {
      if (id) {
        try {
          const response = await customFetch.get(`${API_ENDPOINTS.SERVICES.BASE}/${id}`);
          const service = response.data.data || response.data;
          setSelectedService(service);
          setOpenServiceForm(true);
          setViewMode(true);
        } catch (error) {
          console.error('Error loading service:', error);
          //toast.error(t('services.loadError'));
        }
      }
    };

    loadServiceFromId();
  }, [id]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await customFetch.get(API_ENDPOINTS.SERVICES.BASE, {
        params: {
          search: searchTerm,
          sort: orderBy,
          direction: orderDirection
        }
      });
      
      console.log('Services response:', response);
      
      if (!response || !response.data) {
        throw new Error('Failed to fetch services');
      }

      // Extract services from the response.data.data property
      const services = response.data.data || [];
      console.log('Processed services:', services);
      
      setServices(services);
      setError(null);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSubmit = async (serviceData) => {
    if (!canEdit()) {
      //toast.error(t('services.onlyAdminsCanCreate'));
      return;
    }

    try {
      setLoading(true);
      if (serviceData._id) {
        // Update existing service
        await customFetch.patch(API_ENDPOINTS.SERVICES.UPDATE_SERVICE(serviceData._id), serviceData);
        toast.success(t('services.updateSuccess'));
      } else {
        // Creating new service
        console.log('Creating service with data:', serviceData);
        await customFetch.post(API_ENDPOINTS.SERVICES.BASE, serviceData);
        console.log('Service created:', serviceData);
      }
      await fetchServices(); // Refresh the services list
      setOpenServiceForm(false);
      setSelectedService(null);
      toast.success(serviceData._id ? t('services.serviceUpdated') : t('services.serviceCreated'));
    } catch (err) {
      console.error('Error saving service:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      //toast.error(`${t('services.errorSaving')}: ${errorMessage}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceUpdate = (updatedService) => {
    setServices(prevServices => 
      prevServices.map(service => 
        service._id === updatedService._id ? updatedService : service
      )
    );
  };

  const handleViewService = (service) => {
    setSelectedService(service);
    setViewMode(true);
    setOpenServiceForm(true);
  };

  const handleEditService = (service) => {
    if (!canEdit()) {
      //toast.error(t('services.onlyAdminsCanEdit'));
      return;
    }
    setSelectedService(service);
    setViewMode(false);
    setOpenServiceForm(true);
  };

  const handleDeleteService = async (service) => {
    if (!canEdit()) {
      //toast.error(t('services.onlyAdminsCanDelete'));
      return;
    }

    if (window.confirm(t('services.deleteConfirmation'))) {
      try {
        await customFetch.delete(API_ENDPOINTS.SERVICES.BASE + '/' + service._id);
        await fetchServices();
        toast.success(t('services.serviceDeleted'));
      } catch (err) {
        console.error('Error deleting service:', err);
        //toast.error(t('services.errorDeleting'));
      }
    }
  };

  const handleCloseForm = () => {
    setOpenServiceForm(false);
    setSelectedService(null);
    setViewMode(false);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    handleFilterClose();
  };

  const handleSortSelect = (sortType) => {
    setOrderBy(sortType);
    handleSortClose();
  };

  // Get current user ID from auth context for filtering my services
  const { user } = useAuth();
  const currentUserId = user?._id;
  
  // Get current date for comparing past and future services
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
  
  // Categorize services into My Services, Future Services, and Past Services
  const categorizeServices = (services) => {
    const myServices = [];
    const futureServices = [];
    const pastServices = [];
    
    services.forEach(service => {
      // Convert service date to Date object for comparison
      const serviceDate = service.date ? new Date(service.date) : null;
      serviceDate?.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
      
      // First check if the service is in the past - this takes precedence
      if (serviceDate && serviceDate < currentDate) {
        pastServices.push(service);
        return; // Skip further categorization for past services
      }
      
      // Check if current user is a team member for non-past services
      const isTeamMember = service.team?.some(member => 
        member.user?._id === currentUserId || member.user === currentUserId
      );
      
      // Add to My Services if user is a team member
      if (isTeamMember) {
        myServices.push(service);
      }
      
      // Add to Future Services if the date is in the future or today
      if (serviceDate && serviceDate >= currentDate) {
        futureServices.push(service);
      }
    });
    
    return { myServices, futureServices, pastServices };
  };

  const filteredServices = services
    .filter((service) => {
      const serviceTitle = service?.title || '';
      const serviceCategory = service?.category || '';
      const matchesSearch = serviceTitle.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || serviceCategory === selectedCategory;
      return matchesSearch && matchesCategory;
    });

  const sortedServices = filteredServices
    .sort((a, b) => {
      switch (orderBy) {
        case 'date':
          const dateA = a?.date ? new Date(a.date) : new Date(0);
          const dateB = b?.date ? new Date(b.date) : new Date(0);
          return orderDirection === 'asc' ? dateA - dateB : dateB - dateA;
        case 'title':
          return orderDirection === 'asc' ? (a?.title || '').localeCompare(b?.title || '') : (b?.title || '').localeCompare(a?.title || '');
        case 'category':
          return orderDirection === 'asc' ? (a?.category || '').localeCompare(b?.category || '') : (b?.category || '').localeCompare(a?.category || '');
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{t('services.errorLoadingServices')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title={t('services.title')}
        subtitle={t('services.subtitle')}
        icon={<ServiceIcon />}
        onAddClick={() => {
          setSelectedService(null);
          setViewMode(false);
          setOpenServiceForm(true);
        }}
        showAddButton={canEdit()}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('services.errorLoadingServices')}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : services.length === 0 ? (
        <Box textAlign="center" my={4}>
          <Typography variant="h6" color="text.secondary">
            {t('services.noServicesFound')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('services.clickToAddService')}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Search and Filter Bar */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            mb: 3,
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EventBusyIcon />}
              onClick={() => navigate('/app/blockout-dates')}
              sx={{ mr: 1 }}
            >
              {t('blockoutDates.manageBlockoutDates')}
            </Button>
            {canEdit() && groupedBlockoutsArray.length > 0 && (
              <Button
                variant="text"
                color="warning"
                size="small"
                startIcon={<WarningIcon />}
                endIcon={showUpcomingBlockouts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowUpcomingBlockouts(!showUpcomingBlockouts)}
              >
                {t('blockoutDates.upcomingUnavailabilities', { count: groupedBlockoutsArray.length })}
              </Button>
            )}
            <TextField
              placeholder={t('services.searchServices')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <IconButton onClick={handleFilterClick}>
              <FilterIcon />
            </IconButton>
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
            >
              {['all', 'sunday', 'communion', 'midweek', 'youth', 'women', 'men', 'special'].map((category) => (
                <MenuItem
                  key={category}
                  onClick={() => handleCategorySelect(category)}
                  selected={selectedCategory === category}
                >
                  {t(`services.categories.${category}`)}
                </MenuItem>
              ))}
            </Menu>

            <IconButton onClick={handleSortClick}>
              <SortIcon />
            </IconButton>
            <Menu
              anchorEl={sortAnchorEl}
              open={Boolean(sortAnchorEl)}
              onClose={handleSortClose}
            >
              <MenuItem onClick={() => handleSortSelect('date')} selected={orderBy === 'date'}>
                {t('services.sortByDate')}
              </MenuItem>
              <MenuItem onClick={() => handleSortSelect('title')} selected={orderBy === 'title'}>
                {t('services.sortByTitle')}
              </MenuItem>
              <MenuItem onClick={() => handleSortSelect('category')} selected={orderBy === 'category'}>
                {t('services.sortByCategory')}
              </MenuItem>
            </Menu>
          </Box>

          {/* Upcoming Blockout Dates Preview (admin/leader only) */}
          {canEdit() && showUpcomingBlockouts && groupedBlockoutsArray.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: 2,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                bgcolor: alpha(theme.palette.warning.main, 0.04),
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <WarningIcon sx={{ color: theme.palette.warning.main, mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.warning.dark }}>
                  {t('blockoutDates.upcomingUnavailabilitiesTitle')}
                </Typography>
              </Box>
              {groupedBlockoutsArray.map((group) => {
                const userName = `${group.user.firstName} ${group.user.lastName}`;
                const sortedDates = group.dates.sort((a, b) => new Date(a.date) - new Date(b.date));
                const firstDate = formatBlockoutDate(sortedDates[0].date);
                const lastDate = formatBlockoutDate(sortedDates[sortedDates.length - 1].date);
                const period = sortedDates.length === 1 ? firstDate : `${firstDate} - ${lastDate}`;
                const reason = sortedDates[0].description || t('blockoutDates.noReasonProvided');

                return (
                  <Box
                    key={group.user._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      px: 1.5,
                      mb: 0.5,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      '&:last-child': { mb: 0 },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {userName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {period} — {reason}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', ml: 1, flexShrink: 0 }}>
                      {group.user.phone && (
                        <Tooltip title={t('blockoutDates.sendSms')}>
                          <IconButton
                            size="small"
                            href={`sms:${group.user.phone}`}
                            sx={{ color: theme.palette.success.main }}
                          >
                            <SmsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {group.user.email && (
                        <Tooltip title={t('blockoutDates.sendEmail')}>
                          <IconButton
                            size="small"
                            href={`mailto:${group.user.email}`}
                            sx={{ color: theme.palette.info.main }}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                <Button
                  size="small"
                  onClick={() => navigate('/app/blockout-dates')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('blockoutDates.viewAll')}
                </Button>
              </Box>
            </Paper>
          )}

          {/* Collapsible Tabs */}
          <Box sx={{ mb: 4 }}>
            {/* My Services Tab */}
            <Card 
              sx={{ 
                mb: 2, 
                overflow: 'hidden',
                border: expandedTabs.includes('myServices') ? `2px solid ${theme.palette.primary.main}` : 'none'
              }}
            >
              <Box 
                onClick={() => {
                  setExpandedTabs(prev => 
                    prev.includes('myServices') 
                      ? prev.filter(tab => tab !== 'myServices') 
                      : [...prev, 'myServices']
                  );
                }}
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  bgcolor: expandedTabs.includes('myServices') ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GroupIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6">{t('services.myServices')}</Typography>
                </Box>
                <Chip 
                  label={categorizeServices(sortedServices).myServices.length} 
                  color="primary" 
                  size="small" 
                />
              </Box>
              
              {expandedTabs.includes('myServices') && (
                <Box sx={{ p: 2 }}>
                  {categorizeServices(sortedServices).myServices.length > 0 ? (
                    <Grid container spacing={3}>
                      {categorizeServices(sortedServices).myServices.map((service) => (
                        <Grid item xs={12} sm={6} md={4} key={service._id}>
                          <ServiceCard
                            service={service}
                            onEdit={handleEditService}
                            onView={handleViewService}
                            onDelete={handleDeleteService}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      {t('services.noServicesInThisCategory')}
                    </Typography>
                  )}
                </Box>
              )}
            </Card>
            
            {/* Future Services Tab */}
            <Card 
              sx={{ 
                mb: 2, 
                overflow: 'hidden',
                border: expandedTabs.includes('futureServices') ? `2px solid ${theme.palette.info.main}` : 'none'
              }}
            >
              <Box 
                onClick={() => {
                  setExpandedTabs(prev => 
                    prev.includes('futureServices') 
                      ? prev.filter(tab => tab !== 'futureServices') 
                      : [...prev, 'futureServices']
                  );
                }}
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  bgcolor: expandedTabs.includes('futureServices') ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DateIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                  <Typography variant="h6">{t('services.futureServices')}</Typography>
                </Box>
                <Chip 
                  label={categorizeServices(sortedServices).futureServices.length} 
                  color="info" 
                  size="small" 
                />
              </Box>
              
              {expandedTabs.includes('futureServices') && (
                <Box sx={{ p: 2 }}>
                  {categorizeServices(sortedServices).futureServices.length > 0 ? (
                    <Grid container spacing={3}>
                      {categorizeServices(sortedServices).futureServices.map((service) => (
                        <Grid item xs={12} sm={6} md={4} key={service._id}>
                          <ServiceCard
                            service={service}
                            onEdit={handleEditService}
                            onView={handleViewService}
                            onDelete={handleDeleteService}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      {t('services.noServicesInThisCategory')}
                    </Typography>
                  )}
                </Box>
              )}
            </Card>
            
            {/* Past Services Tab */}
            <Card 
              sx={{ 
                mb: 2, 
                overflow: 'hidden',
                border: expandedTabs.includes('pastServices') ? `2px solid ${theme.palette.text.secondary}` : 'none'
              }}
            >
              <Box 
                onClick={() => {
                  setExpandedTabs(prev => 
                    prev.includes('pastServices') 
                      ? prev.filter(tab => tab !== 'pastServices') 
                      : [...prev, 'pastServices']
                  );
                }}
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  bgcolor: expandedTabs.includes('pastServices') ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TimeIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
                  <Typography variant="h6">{t('services.pastServices')}</Typography>
                </Box>
                <Chip 
                  label={categorizeServices(sortedServices).pastServices.length} 
                  color="default" 
                  size="small" 
                />
              </Box>
              
              {expandedTabs.includes('pastServices') && (
                <Box sx={{ p: 2 }}>
                  {categorizeServices(sortedServices).pastServices.length > 0 ? (
                    <Grid container spacing={3}>
                      {categorizeServices(sortedServices).pastServices.map((service) => (
                        <Grid item xs={12} sm={6} md={4} key={service._id}>
                          <ServiceCard
                            service={service}
                            onEdit={handleEditService}
                            onView={handleViewService}
                            onDelete={handleDeleteService}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      {t('services.noServicesInThisCategory')}
                    </Typography>
                  )}
                </Box>
              )}
            </Card>
          </Box>
        </>
      )}

      {/* Service Form Dialog */}
      {openServiceForm && (
        <ServiceForm
          open={openServiceForm}
          onClose={handleCloseForm}
          onSubmit={handleServiceSubmit}
          customFetch={customFetch}
          initialData={selectedService}
          readOnly={viewMode && !canEdit()}
          onServiceUpdate={handleServiceUpdate}
        />
      )}
      
      {/* ... other JSX ... */}
    </Box>
  );
};

export default Services;
